import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';

const TYPE_COLORS = {
  bug_intake: 'bg-red-100 text-red-700',
  feature_intake: 'bg-blue-100 text-blue-700',
  compliance_intake: 'bg-orange-100 text-orange-700',
  legal_intake: 'bg-amber-100 text-amber-700',
  harm_intake: 'bg-red-200 text-red-800',
  points_info: 'bg-green-100 text-green-700',
  welcome: 'bg-gray-100 text-gray-600',
};

export default function ContributionsPage() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [profile, setProfile] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetch('/api/contributor/profile', { credentials: 'include' })
      .then(r => r.json())
      .then(d => d.data && setProfile(d.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function sendMessage(e) {
    e.preventDefault();
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput('');
    setSending(true);

    // Optimistic user message
    setMessages(prev => [...prev, { role: 'user', text, timestamp: new Date().toISOString() }]);

    try {
      const res = await fetch('/api/contributor/agent/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: text,
          conversationId,
          context: { page: window.location.pathname },
        }),
      });
      const data = await res.json();
      if (data.success) {
        setConversationId(data.data.conversationId);
        setMessages(prev => [...prev, { role: 'assistant', ...data.data.response, timestamp: new Date().toISOString() }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', text: 'Sorry, something went wrong. Please try again.', type: 'error', timestamp: new Date().toISOString() }]);
    }
    setSending(false);
  }

  async function handleAction(action) {
    if (action.type === 'navigate') {
      window.location.href = action.route;
    } else if (action.type === 'create_proposal') {
      const lastUserMsg = [...messages].reverse().find(m => m.role === 'user');
      if (!lastUserMsg) return;
      try {
        const res = await fetch('/api/contributor/proposals', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            type: action.proposalType,
            title: lastUserMsg.text.substring(0, 200),
            description: lastUserMsg.text,
          }),
        });
        const data = await res.json();
        if (data.success) {
          setMessages(prev => [...prev, {
            role: 'assistant',
            text: `Proposal submitted and sent to the committee for review. You earned **5 contribution points**.`,
            type: 'proposal_created',
            timestamp: new Date().toISOString(),
          }]);
        }
      } catch {}
    }
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Contributions</h1>
        <p className="text-sm text-gray-500">Report bugs, suggest features, and earn ownership in Legalia</p>
      </div>

      {/* Stats */}
      {profile && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-amber-50 rounded-xl p-4">
            <p className="text-[10px] font-medium text-amber-500 uppercase">Ownership Points</p>
            <p className="text-2xl font-bold text-amber-900">{(profile.totalImpact || 0).toLocaleString()}</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-4">
            <p className="text-[10px] font-medium text-blue-500 uppercase">Contributions</p>
            <p className="text-2xl font-bold text-blue-900">{(profile.totalContributions || 0).toLocaleString()}</p>
          </div>
        </div>
      )}

      {/* Chat */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-700">Contribution Agent</h2>
          <p className="text-xs text-gray-400">Report issues, suggest improvements, ask about your contributions</p>
        </div>

        <div className="h-96 overflow-y-auto p-5 space-y-4">
          {messages.length === 0 && (
            <div className="text-center text-sm text-gray-400 py-12">
              <p className="mb-2">Tell me about a bug, feature idea, or compliance concern.</p>
              <p className="text-xs">Every contribution earns ownership points in Legalia.</p>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                msg.role === 'user'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                <p className="text-sm whitespace-pre-wrap">{msg.text}</p>

                {/* Action buttons */}
                {msg.actions && msg.actions.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {msg.actions.map((action, j) => (
                      <button
                        key={j}
                        onClick={() => handleAction(action)}
                        className="px-3 py-1.5 text-xs font-medium bg-white text-gray-800 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                )}

                {msg.type && msg.role === 'assistant' && TYPE_COLORS[msg.type] && (
                  <span className={`inline-block mt-2 text-[10px] px-1.5 py-0.5 rounded-full font-medium ${TYPE_COLORS[msg.type]}`}>
                    {msg.type.replace('_', ' ')}
                  </span>
                )}
              </div>
            </div>
          ))}

          {sending && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-2xl px-4 py-3">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={sendMessage} className="px-5 py-3 border-t border-gray-100 flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Report a bug, suggest a feature, or ask about your contributions..."
            className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            disabled={sending}
          />
          <button
            type="submit"
            disabled={sending || !input.trim()}
            className="px-5 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-xl hover:bg-primary-700 disabled:opacity-40 transition-colors"
          >
            Send
          </button>
        </form>
      </div>
    </div>
  );
}
